import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ConstructionIcon from '@mui/icons-material/Construction';
import { CircularProgress, Alert } from '@mui/material';
import { SEVERITY_LEVELS } from '../../utils/trlConstants';
import { useTRLs } from '../../hooks/useTRLs';
import { computeTRLStats } from '../../../../services/trlService';
import { CONDITION_STATUS_ORDER, conditionStatusLabel, conditionStatusColor } from '../../utils/trlConstants';
import OrgSwitcher from '../../components/OrgSwitcher';

/**
 * TRL System — Mindmap branch 3, "Inputs → Org Data → TRL System"
 *   - TRLs Identified
 *   - TRLs Fixed/Removed (REF)
 * LIVE: reads Hugo_TRLs in real time (persona-scoped).
 */
function TRLSystem() {
  const { trls, loading } = useTRLs({ limit: 500 });
  const stats = computeTRLStats(trls);

  // Counts by type
  // P2-22: Drift is a type of its own (D06) — the by-type chart shows all four, and the split
  // Drift vs Traps/Roadblocks/Landmines (accumulation) uses the stored `kind`.
  const typeCounts = ['drift', 'trap', 'roadblock', 'landmine'].map((t) => ({
    name: t.charAt(0).toUpperCase() + t.slice(1),
    value: trls.filter((x) => x.type === t && !['withdrawn', 'cancelled'].includes(x.status)).length,
  }));
  const statusCounts = CONDITION_STATUS_ORDER.filter((st) => !['withdrawn', 'cancelled'].includes(st)).map((st) => ({
    status: st,
    name: conditionStatusLabel(st),
    value: trls.filter((x) => x.status === st).length,
    color: conditionStatusColor(st),
  }));

  // Severity distribution (most-calibrated severity wins: DH > FLS > ITM)
  const sevCounts = SEVERITY_LEVELS.map((s) => ({
    name: `Sev ${s.value}`,
    value: trls.filter((x) => (x.severityDH || x.severityFLS || x.severityITM) === s.value).length,
    color: s.color,
  }));

  const refCount = trls.reduce((acc, t) => {
    t.refActions?.forEach((a) => {
      acc[a.type] = (acc[a.type] || 0) + 1;
    });
    return acc;
  }, {});

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <GpsFixedIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4">OE Condition Analytics</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <OrgSwitcher />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Live view of every OE Condition identified — Drift and the accumulation of Traps, Roadblocks, and Landmines — and the Reverse / Eliminate / Fortify (REF) actions taken to address them. Updates in real time as entries are logged.
      </Typography>

      {trls.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No OE Conditions logged yet. Entries submitted through the OE Condition Entry Portal appear here the moment they land.
        </Alert>
      )}

      {/* Top KPI cards (P2-22: by-status counts use the Beta-1 status set) */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <Card><CardContent>
            <Typography variant="overline" color="text.secondary">Total OE Conditions</Typography>
            <Typography variant="h3" fontWeight={700}>{stats.total}</Typography>
            <Typography variant="caption" color="text.secondary">Identified to date</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card><CardContent>
            <Typography variant="overline" color="text.secondary">Drift · Accumulation</Typography>
            <Typography variant="h3" fontWeight={700}><Box component="span" sx={{ color: '#6a1b9a' }}>{stats.byKind.drift}</Box> <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.6em' }}>·</Box> <Box component="span" sx={{ color: '#ef6c00' }}>{stats.byKind.trl}</Box></Typography>
            <Typography variant="caption" color="text.secondary">Drift vs Traps / Roadblocks / Landmines</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card><CardContent>
            <Typography variant="overline" color="text.secondary">In review</Typography>
            <Typography variant="h3" fontWeight={700} sx={{ color: '#1976d2' }}>{stats.submitted + stats.reviewL1 + stats.underReview}</Typography>
            <Typography variant="caption" color="text.secondary">Pending {stats.submitted} · L1 {stats.reviewL1} · L2 {stats.underReview}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card><CardContent>
            <Typography variant="overline" color="text.secondary">Approved/Active · Resolved</Typography>
            <Typography variant="h3" fontWeight={700}><Box component="span" sx={{ color: '#2e7d32' }}>{stats.approved + stats.resolutionPending}</Box> <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.6em' }}>·</Box> <Box component="span" sx={{ color: '#455a64' }}>{stats.resolved}</Box></Typography>
            <Typography variant="caption" color="text.secondary">REF actions in progress · closed out</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ bgcolor: '#fff3e0' }}><CardContent>
            <Typography variant="overline" color="text.secondary">Critical (Sev IV)</Typography>
            <Typography variant="h3" fontWeight={700} sx={{ color: '#c62828' }}>{stats.severityIV}</Typography>
            <Typography variant="caption" color="text.secondary">Active highest-severity items</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* TRLs by type */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
                {statusCounts.filter((sc) => sc.value > 0).map((sc) => (
                  <Chip key={sc.status} size="small" label={`${sc.name}: ${sc.value}`} sx={{ bgcolor: `${sc.color}22`, color: sc.color, fontWeight: 700 }} />
                ))}
              </Stack>
              <Typography variant="h6" gutterBottom>OE Conditions Identified — by Type</Typography>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={typeCounts} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} label>
                      <Cell fill="#ef6c00" />
                      <Cell fill="#1976d2" />
                      <Cell fill="#6a1b9a" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Severity distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Severity Distribution</Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {sevCounts.map((s) => (
                  <Box key={s.name}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">{s.name}</Typography>
                      <Typography variant="caption" fontWeight={700}>{s.value}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(s.value / Math.max(stats.total, 1)) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': { bgcolor: s.color },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* REF actions */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <ConstructionIcon color="success" />
                <Typography variant="h6">REF Actions Taken</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {stats.refActionsTotal} total actions logged
              </Typography>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>Reverse</Typography>
                  <Typography variant="caption" color="text.secondary">Reverse the drift</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#0288d1' }}>{refCount.reduce || 0}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" fontWeight={700}>Eliminate</Typography>
                  <Typography variant="caption" color="text.secondary">Eliminate the landmine(s)</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32' }}>{refCount.eliminate || 0}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" fontWeight={700}>Fortify</Typography>
                  <Typography variant="caption" color="text.secondary">Fortify associated defense(s)</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#6a1b9a' }}>{refCount.fortify || 0}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TRLSystem;
